import { useMutation, useQueryClient } from '@tanstack/react-query';
import useHandleAppException, { AppExceptionResponse } from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import snackbarStore from '../../../stores/Snackbar.store';
import { satisfactionSurveySettingsRepository } from './satisfactionSurveySettings.repository';
import {
  SatisfactionSurveySettings,
  UpdateSatisfactionSurveySettings,
} from './satisfactionSurveySettings.types';

interface UseUpdateSatisfactionSurveySettingsParams {
  queryKey: unknown[];
  onError?: () => void;
}

export const useUpdateSatisfactionSurveySettings = ({
  queryKey,
  onError,
}: UseUpdateSatisfactionSurveySettingsParams) => {
  const { translate } = useTranslation();
  const { handleErrorResponse } = useHandleAppException();
  const queryClient = useQueryClient();

  return useMutation({
    scope: { id: 'satisfaction-survey-settings' },
    mutationFn: async (values: UpdateSatisfactionSurveySettings) => {
      const response = await satisfactionSurveySettingsRepository.update(values);

      if (!response.ok) {
        throw response;
      }

      return response.data as SatisfactionSurveySettings;
    },
    onMutate: async (values) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<SatisfactionSurveySettings>(queryKey);

      if (previous) {
        queryClient.setQueryData(queryKey, { ...previous, ...values });
      }

      return { previous };
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKey, updated);
      snackbarStore.handleMessage({
        type: 'success',
        message: translate(LangEnum.GLOBAL_SAVED_SUCCESSFULLY),
      });
    },
    onError: (error, _values, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }

      const response = error as unknown as AppExceptionResponse;
      const apiMessage = response?.data ? handleErrorResponse(response) : '';

      snackbarStore.handleMessage({
        type: 'error',
        message: apiMessage || translate(LangEnum.GLOBAL_ERROR_MESSAGE),
      });
      queryClient.invalidateQueries({ queryKey });
      onError?.();
    },
  });
};
